import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../lib/api';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { KeyRound } from 'lucide-react';
import { getInitials, getAvatarColor, roleLabels, formatDate } from '../lib/utils';

export default function SettingsPage() {
  const { user } = useAuth();
  const [authConfig, setAuthConfig] = useState(null);

  // `/auth/config` ne renvoie que l'issuer et le clientId : un client OIDC
  // public n'a pas de secret, ces valeurs figurent déjà dans les URL de
  // redirection. Rien de confidentiel ne descend au navigateur.
  useEffect(() => {
    authAPI.getConfig()
      .then(r => setAuthConfig(r.data))
      .catch(() => setAuthConfig(null));
  }, []);

  return (
    <div className="space-y-6 max-w-2xl animate-fade-in" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Paramètres</h1>
        <p className="text-sm text-muted-foreground mt-0.5">Gérez vos préférences et votre profil</p>
      </div>

      {/* Profile Card */}
      <div className="bg-card border border-border rounded-xl p-6 shadow-sm space-y-6">
        <div>
          <h2 className="text-base font-semibold text-foreground">Profil</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Vos informations de compte</p>
        </div>

        <div className="flex items-center gap-4">
          <Avatar className="h-16 w-16">
            <AvatarFallback className={`${getAvatarColor(user?.name)} text-lg font-bold`}>
              {getInitials(user?.name)}
            </AvatarFallback>
          </Avatar>
          <div>
            <h3 className="text-base font-semibold text-foreground">{user?.name}</h3>
            <p className="text-sm text-muted-foreground">{user?.email}</p>
            <span className="inline-block mt-1.5 text-xs px-2.5 py-1 rounded-full font-medium bg-primary/10 text-primary">
              {roleLabels[user?.role]}
            </span>
          </div>
        </div>

        <div className="grid gap-4">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Nom</Label>
            <Input value={user?.name || ''} disabled className="mt-1 bg-muted/40" />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Email</Label>
            <Input value={user?.email || ''} disabled className="mt-1 bg-muted/40" />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Rôle</Label>
            <Input value={roleLabels[user?.role] || ''} disabled className="mt-1 bg-muted/40" />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Membre depuis</Label>
            <Input value={user?.created_at ? formatDate(user.created_at) : '—'} disabled className="mt-1 bg-muted/40" />
          </div>
        </div>
      </div>

      {/* Auth Card */}
      <div className="bg-card border border-border rounded-xl p-6 shadow-sm space-y-4">
        <div>
          <h2 className="text-base font-semibold text-foreground">Authentification</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Authentification unique ELIKA (Keycloak)</p>
        </div>

        <div className="flex items-center justify-between p-4 bg-muted/40 rounded-lg">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <KeyRound size={18} className="text-primary" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">Keycloak — realm ELIKA</p>
              <p className="text-xs text-muted-foreground">
                {authConfig?.configured
                  ? 'Configuré et actif — Microsoft Entra ID fédéré'
                  : 'Configuration indisponible'}
              </p>
            </div>
          </div>
          <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${authConfig?.configured ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-muted text-muted-foreground'}`}>
            {authConfig?.configured ? 'Actif' : '—'}
          </span>
        </div>

        {authConfig?.configured && (
          <div className="grid gap-3 text-sm">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Émetteur (issuer)</p>
              <p className="font-mono bg-muted p-2.5 rounded-lg text-xs break-all text-foreground">
                {authConfig.issuer}
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Client ID</p>
              <p className="font-mono bg-muted p-2.5 rounded-lg text-xs break-all text-foreground">
                {authConfig.client_id}
              </p>
            </div>
          </div>
        )}

        <div className="p-4 bg-muted/40 border border-border rounded-lg">
          <p className="text-sm text-muted-foreground">
            Les comptes et les droits sont administrés dans le backoffice du{' '}
            <a href="https://portal.elikateam.com" target="_blank" rel="noreferrer"
               className="text-primary hover:underline font-medium">portail ELIKA</a>.
            Le rôle est resynchronisé depuis Keycloak à chaque connexion.
          </p>
        </div>
      </div>

      {/* App Info */}
      <div className="bg-card border border-border rounded-xl p-6 shadow-sm">
        <h2 className="text-base font-semibold text-foreground mb-4">À propos</h2>
        <div className="space-y-3 text-sm">
          {[
            { label: 'Application', value: 'SalePulse' },
            { label: 'Version', value: '1.0.0' },
            { label: 'Environnement', value: 'Production' },
          ].map(({ label, value }) => (
            <div key={label} className="flex justify-between items-center py-2 border-b border-border last:border-0">
              <span className="text-muted-foreground">{label}</span>
              <span className="font-semibold text-foreground">{value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
